// 新建任务 Markdown 模板 / Markdown templates for new tasks.
// 提供 Single 与 A/B 草稿模板，并支持下载为本地 .md 文件。
// Provides Single and A/B draft templates and downloads them as local .md files.

import type { Mode } from "@/lib/types";

/**
 * 生成 Single 模式 PMF 模板 / Build the Single-mode PMF template.
 */
export function singleTemplate(): string {
  return [
    "---",
    "title: PMF Validation Task Template",
    "product_name: Untitled Product",
    "target_user: Core target users",
    "channel: generic",
    "vertical: saas",
    "---",
    "",
    "# PMF Validation Task Template",
    "",
    "## Product Overview",
    "Describe the product, target users, and the core PMF hypothesis you want to validate in one concise paragraph.",
    "",
    "## Positioning",
    "Describe how users solve the problem today, where current options fall short, and what makes this product meaningfully different.",
    "",
    "## Target Users",
    "- Primary users:",
    "- Key use cases:",
    "- Purchase or trial triggers:",
    "",
    "## Differentiated Value",
    "- Value prop 1:",
    "- Value prop 2:",
    "- Value prop 3:",
    "",
    "## Validation Question",
    "What is the most important PMF question this simulation should answer?",
    "",
  ].join("\n");
}

/**
 * 生成 A/B 模式 PMF 模板 / Build the A/B-mode PMF templates.
 */
export function abTemplates(): { a: string; b: string } {
  return {
    a: variantTemplate("A", "Variant A"),
    b: variantTemplate("B", "Variant B"),
  };
}

/**
 * 下载指定模式的模板 / Download the template for the selected mode.
 */
export function downloadTemplate(mode: Mode, slot: "A" | "B" = "A"): void {
  if (mode === "ab") {
    const templates = abTemplates();
    const content = slot === "B" ? templates.b : templates.a;
    downloadMarkdown(`pmf-ab-${slot.toLowerCase()}-template.md`, content);
    return;
  }
  downloadMarkdown("pmf-single-template.md", singleTemplate());
}

/**
 * 以 Markdown 文件形式下载内容 / Download content as a Markdown file.
 */
export function downloadMarkdown(filename: string, content: string): void {
  const safeFilename = filename.toLowerCase().endsWith(".md") ? filename : `${filename}.md`;
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = safeFilename;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// 生成单个 A/B 变体模板 / Build one A/B variant template.
function variantTemplate(slot: "A" | "B", label: string): string {
  return [
    "---",
    `title: PMF ${label} Validation Template`,
    `product_name: Untitled Product ${slot}`,
    `variant: ${slot}`,
    "target_user: Core target users",
    "channel: generic",
    "vertical: saas",
    "---",
    "",
    `# PMF ${label} Validation Template`,
    "",
    "## Variant Hypothesis",
    `Describe ${label}'s positioning, pricing, or channel hypothesis.`,
    "",
    "## Positioning",
    "Describe how this variant differs from competitors or from the other plan.",
    "",
    "## Target Users",
    "- Primary users:",
    "- Use cases:",
    "- Decision triggers:",
    "",
    "## Differentiated Value",
    "- Value prop 1:",
    "- Value prop 2:",
    "",
    "## Validation Question",
    `Would users prefer ${label}, and why?`,
    "",
  ].join("\n");
}
